import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import type { ItemState } from './@types/ItemState'
import type { SliceItems } from './@types/SliceItems'
import type { EntitySliceMethods } from './@types/SliceMutators'
import { DEFAULT_DATA } from './constants'
import mutateItem from './mutateItem'

type SliceConfig = {
  name: string
}

export function createEntitiesSlice<T extends { id: string }>(config: SliceConfig) {
  type EntityState = ItemState<T>
  type EntityMethods = EntitySliceMethods<T>
  type EntityItems = SliceItems<T, EntityState>
  type EntitySlice = EntityMethods & EntityItems

  return create<EntitySlice>()(
    devtools(
      (set, get) => ({
        items: DEFAULT_DATA as EntityItems['items'],
        setItemLoadStart: (id, loaded = false) => {
          set(
            mutateItem(
              id,
              Object.fromEntries(
                Object.entries({
                  loaded,
                  loading: true,
                }).filter(([, value]) => value !== null),
              ),
            ),
            false,
            `${config.name}/setItemLoadStart`,
          )
        },
        setItemLoadSuccess: (id, data) => {
          set(
            mutateItem(id, {
              error: undefined,
              loaded: true,
              loading: false,
              data,
            }),
            false,
            `${config.name}/setItemLoadSuccess`,
          )
        },
        setItemLoadFailure: (id, error) => {
          set(
            mutateItem(id, {
              loading: false,
              error,
            }),
            false,
            `${config.name}/setItemLoadFailure`,
          )
        },
        setItemLoading: (id, loading) => {
          set(mutateItem(id, { loading }), false, `${config.name}/setItemLoading`)
        },
        setItemLoaded: (id, loaded) => {
          set(mutateItem(id, { loaded }), false, `${config.name}/setItemLoaded`)
        },
        patchItemData: (id, data) => {
          set(
            currentState =>
              mutateItem<T>(id, {
                data: {
                  ...currentState.items[id]?.data,
                  ...data,
                },
              })(currentState),
            false,
            `${config.name}/patchItemData`,
          )
        },
        getItem: id => {
          return get().items[id]
        },
        resetItems: () => {
          set({ items: DEFAULT_DATA as EntityItems['items'] }, false, `${config.name}/resetItems`)
        },
      }),
      {
        name: config.name,
      },
    ),
  )
}
